/* eslint-disable @typescript-eslint/no-explicit-any */
import { FiltersType, OfferType, ReferenceLocation, SearchTabs, Sorting } from '@/types';
import { lzCompress, lzDecompress } from '@/lib/lzString';

// ---------------------------------------------------------------------------
// Pure (server-safe) filter (de)serialization.
// Extracted from useFilters so it can be reused on the server (SSR/SEO).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Serialize
// ---------------------------------------------------------------------------
export const serialize = {
  numArray: (arr: number[]) => arr.join(','),
  strArray: (arr: string[]) => arr.join(','),
  codes: (arr: Array<{ code: string }>) => [...new Set(arr.map((a) => a.code))].join(','),
  string: (v: string) => v,
  number: (v: number) => String(v),
  mapArea: (area: FiltersType['mapArea']): string => {
    if (!area) return '';
    const r = (n: number) => Math.round(n * 1e6) / 1e6;
    if (area.lonLatGeometry?.length) {
      return area.lonLatGeometry
        .map(([lng, lat]: [number, number]) => `${r(lng)},${r(lat)}`)
        .join('|');
    }
    if (area.lonLatLeftBottom && area.lonLatRightTop) {
      const [x1, y1] = area.lonLatLeftBottom;
      const [x2, y2] = area.lonLatRightTop;
      return `${r(x1)},${r(y1)},${r(x2)},${r(y2)}`;
    }
    return '';
  },
  admunits: (admunits: FiltersType['admunits']) =>
    lzCompress((admunits ?? []).map((a: { id: string }) => a.id)),
  locations: (locs: FiltersType['locations']): string =>
    locs?.length
      ? lzCompress(
          locs.map((loc) => ({
            type: loc.type,
            code: loc.code,
            name: loc.name,
            ...(loc.type === 'PLACE' && loc.postcode ? { postcode: loc.postcode } : {}),
          })),
        )
      : '',
};

// ---------------------------------------------------------------------------
// Deserialize
// ---------------------------------------------------------------------------
export const deserialize = {
  numArray: (str: string | null): number[] => {
    if (!str) return [];
    return str.split(',').filter(Boolean).map(Number);
  },
  strArray: (str: string | null): string[] => {
    if (!str) return [];
    return str.split(',').filter(Boolean);
  },
  codes: (str: string | null) => {
    if (!str) return [];
    return str
      .split(',')
      .filter(Boolean)
      .map((code) => ({ code, title: '', count: 0 }));
  },
  number: (str: string | null) => (str ? Number(str) : 0),
  mapArea: (str: string | null): FiltersType['mapArea'] => {
    if (!str) return undefined;
    if (str.includes('|')) {
      const lonLatGeometry = str.split('|').map((pt) => {
        const [lng, lat] = pt.split(',').map(Number);
        return [lng, lat] as [number, number];
      });
      const lngs = lonLatGeometry.map(([lng]) => lng);
      const lats = lonLatGeometry.map(([, lat]) => lat);
      const lonLatLeftBottom: [number, number] = [Math.min(...lngs), Math.min(...lats)];
      const lonLatRightTop: [number, number] = [Math.max(...lngs), Math.max(...lats)];
      return { lonLatGeometry, lonLatLeftBottom, lonLatRightTop };
    }
    const parts = str.split(',').map(Number);
    if (parts.length === 4 && parts.every(isFinite)) {
      return { lonLatLeftBottom: [parts[0], parts[1]], lonLatRightTop: [parts[2], parts[3]] };
    }
    return undefined;
  },
  admunits: (str: string | null): FiltersType['admunits'] => {
    if (!str) return undefined;
    const ids = lzDecompress(str);
    if (!Array.isArray(ids) || !ids.length) return undefined;
    // Names are not stored in URL — widget fires onSelectionChange on init and fills them in
    return (ids as string[]).map((id) => ({ id, name: '', parent_name: '' }));
  },
  locations: (str: string | null): FiltersType['locations'] => {
    if (!str) return [];
    const parsed = lzDecompress(str);
    if (!Array.isArray(parsed)) return [];
    return parsed as ReferenceLocation[];
  },
};

// ---------------------------------------------------------------------------
// Parse all filters from URLSearchParams
// ---------------------------------------------------------------------------
export function parseFilters(p: URLSearchParams): FiltersType {
  return {
    currentPage: deserialize.number(p.get('page')),
    offerTypeValue: (p.get('offerType') as OfferType) || OfferType.SELL,
    categoriesValue: deserialize.codes(p.get('categories')),
    roomNumberValue: deserialize.numArray(p.get('rooms')),
    priceValue: deserialize.numArray(p.get('price')),
    rentPriceValue: deserialize.numArray(p.get('rentPrice')),
    unitListValue: deserialize.strArray(p.get('unitList')),
    equipmentValue: deserialize.codes(p.get('equipmentValue')),
    areaValue: deserialize.numArray(p.get('areaValue')),
    powerConsumptionClasses: deserialize.strArray(p.get('hwbClass')),
    energyClassFactors: deserialize.strArray(p.get('fgeeClass')),
    locations: deserialize.locations(p.get('locations')),
    itemsPerPage: deserialize.number(p.get('perPage')) || 25,
    tabs: (p.get('tabs') as SearchTabs) || SearchTabs.LIST,
    sorting: (p.get('sorting') as Sorting) || 'MODIFICATION_DATE',
    mapArea: deserialize.mapArea(p.get('mapArea')),
    admunits: deserialize.admunits(p.get('admunits')),
    keywords: p.get('search') || undefined,
  };
}

// ---------------------------------------------------------------------------
// Build query string from filters (no encoding of safe chars)
// ---------------------------------------------------------------------------
export function buildQs(
  f: FiltersType,
  existing: URLSearchParams,
  pathnameOverride: string | undefined,
): string {
  const entries: [string, string][] = [];

  const add = (key: string, value: any, serializer: (v: any) => string) => {
    if (
      value === '' ||
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    )
      return;
    entries.push([key, serializer(value)]);
  };

  add('page', f.currentPage, serialize.number);
  add('offerType', f.offerTypeValue, serialize.string);
  add('categories', f.categoriesValue, serialize.codes);
  add('rooms', f.roomNumberValue, serialize.numArray);
  add('price', f.priceValue, serialize.numArray);
  add('rentPrice', f.rentPriceValue, serialize.numArray);
  add('unitList', f.unitListValue, serialize.strArray);
  add('equipmentValue', f.equipmentValue, serialize.codes);
  add('areaValue', f.areaValue, serialize.numArray);
  add('hwbClass', f.powerConsumptionClasses, serialize.strArray);
  add('fgeeClass', f.energyClassFactors, serialize.strArray);
  add('locations', f.locations, serialize.locations);
  add('perPage', f.itemsPerPage, serialize.number);
  add('tabs', f.tabs, serialize.string);
  add('sorting', f.sorting, serialize.string);
  add('mapArea', f.mapArea, serialize.mapArea);
  add('admunits', f.admunits, serialize.admunits);
  add('search', f.keywords, serialize.string);

  // Preserve unknown params (e.g. openedProperty, officeId, search-profile)
  if (!pathnameOverride) {
    const allKnownKeys = new Set([
      'page',
      'offerType',
      'categories',
      'rooms',
      'price',
      'rentPrice',
      'unitList',
      'equipmentValue',
      'areaValue',
      'hwbClass',
      'fgeeClass',
      'locations',
      'perPage',
      'tabs',
      'sorting',
      'mapArea',
      'admunits',
      'search',
    ]);
    existing.forEach((v, k) => {
      if (!allKnownKeys.has(k)) entries.push([k, v]);
    });
  }

  return entries.map(([k, v]) => `${k}=${v}`).join('&');
}
